/**
 * The GA4 tab (spec §3.5, R15).
 * - Four tiles: Sessions, Engaged sessions, Web conversions, Share from paid.
 *   The two static ones read "ไม่ได้รายงาน".
 * - A chart of the site's daily sessions and web conversions beside total ad
 *   spend for the same days:
 *   - ad spend is bars on the left axis, in the page currency;
 *   - the site's two counts are lines on the right axis;
 *   - a null bucket is a gap, never 0;
 *   - the grain is box 4's (daily up to 14 days, else ISO weeks from Monday).
 * - GA4 counts the full window, with no cut (R15), so no tile carries the
 *   cut chip.
 *
 * @module components/channels/Ga4Panel
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Bar, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { AXIS_PROPS, CHART_HEIGHT, GRID_PROPS, LEGEND_PROPS, TOOLTIP_PROPS, categoryTick, compactTick } from '@/components/looker/charts/chartTheme';
import { colorAt } from '@/components/charts/chartPalette';
import { fmtCount, fmtMoney } from '@/services/overview/overviewFormat';
import type { Ga4View } from '@/services/channels/channelTypes';
import { PlatformTiles } from './PlatformTiles';

/**
 * Palette slot 2 (#94F53A) fails the lightness band (1.33:1 on white) and is
 * too faint for a 2 px line. The third series takes slot 4 (#4667FF), which
 * passes beside slots 0 and 1 (dataviz validator, 2026-10-14).
 */
const WEB_CONVERSIONS_COLOR = colorAt(4);

export const Ga4Panel: React.FC<{ view: Ga4View; currency: string | null }> = ({ view, currency }) => {
  const { t } = useTranslation();
  const spendName = t('channels.ga4.adSpend');
  return (
    <div className="space-y-4">
      <PlatformTiles platform="analytics" tiles={view.tiles} currency={currency} cutApplied={false} />
      <figure data-trend-grain={view.grain} className="min-w-0">
        <figcaption className="text-sm font-medium text-ink-950">{t('channels.ga4.chartTitle')}</figcaption>
        <p className="mb-2 text-xs text-neutral-700">{view.grain === 'day' ? t('overview.trend.daily') : t('overview.trend.weekly')}</p>
        <div style={{ height: CHART_HEIGHT }}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={view.points} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
              <CartesianGrid {...GRID_PROPS} />
              <XAxis dataKey="key" {...AXIS_PROPS} tickFormatter={categoryTick(t)} />
              <YAxis yAxisId="spend" {...AXIS_PROPS} tickFormatter={compactTick} />
              <YAxis yAxisId="site" orientation="right" {...AXIS_PROPS} tickFormatter={compactTick} />
              <Tooltip
                {...TOOLTIP_PROPS}
                labelFormatter={categoryTick(t)}
                formatter={(v: unknown, name: unknown) => {
                  const n = typeof v === 'number' ? v : null;
                  return name === spendName ? fmtMoney(n, currency) : fmtCount(n);
                }}
              />
              <Legend {...LEGEND_PROPS} />
              <Bar yAxisId="spend" dataKey="adSpend" name={spendName} fill={colorAt(0)} radius={[4, 4, 0, 0]} isAnimationActive={false} />
              <Line yAxisId="site" dataKey="sessions" name={t('channels.tile.sessions')} stroke={colorAt(1)} strokeWidth={2} dot={false} connectNulls={false} isAnimationActive={false} />
              <Line yAxisId="site" dataKey="webConversions" name={t('channels.tile.webConversions')} stroke={WEB_CONVERSIONS_COLOR} strokeWidth={2} dot={false} connectNulls={false} isAnimationActive={false} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </figure>
    </div>
  );
};
